/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useMemo, useState } from 'react';
import { FunctionComponent, useCallback } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useDispatch } from 'react-redux';
import ReactDatePicker from 'react-datepicker';
import { openDialog } from '../../../../../../app/dialog/dialog.actions';
import { dndBoard } from '../../../../../../app/page/page-storage/meeting-boards/meeting-boards.actions';
import { boardSelectors } from '../../../../../../app/resource/boards/board/board.selectors';
import { IBoard } from '../../../../../../app/resource/boards/board/board.types';
import { useSelect, useSelectWithParams } from '../../../../../../utils/selector.utils';
import { IBoardFormProps } from '../../../forms/BoardForm/types';
import { IBoardsProps } from './types';
import { filterTasks, filterTasksByDueDate } from '../../../../../../app/page/boards/boards.actions';
import { boardTasksSelectors } from '../../../../../../app/page/boards/boards.selectors';
import { authSelectors } from '../../../../../../app/auth/auth.selectors';
import { userSelectors } from '../../../../../../app/resource/user/user.selectors';
import { meetingSimpleSelectors } from '../../../../../../app/resource/meeting-simple/meeting-simple.selectors';
import { IMeetingSimple } from '../../../../../../app/resource/meeting-simple/meeting-simple.types';
import { isMemberPartOfMeeting } from '../../../../../../utils/user.utils';
import { FaArchive } from 'react-icons/fa';

export const Boards: FunctionComponent<IBoardsProps> = ({
  boardIds,
  archivedBoardIds,
  showCreateButton = true,
  showAllBoards,
  meetingId,
  currentBoardId,
}) => {
  const loggedUserId = useSelect(authSelectors.selectLoggedUser);
  const loggedUser = useSelectWithParams(userSelectors.selectResourceById, loggedUserId ?? '');

  const meeting = (useSelectWithParams(
    meetingSimpleSelectors.selectResourceById,
    meetingId,
  ) as unknown) as IMeetingSimple;

  const dispatch = useDispatch();
  const boards = useSelectWithParams(boardSelectors.selectResourcesById, boardIds) as IBoard[];
  const archivedBoards = useSelectWithParams(boardSelectors.selectResourcesById, archivedBoardIds ?? []) as IBoard[];
  const filterText = useSelect(boardTasksSelectors.selectFreetextValue);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const dueDateFilter = useSelect(boardTasksSelectors.selectFilterDate);

  const inputAnimationStyles = {
    height: '44px',
    width: isFocused ? '320px' : '200px',
    paddingLeft: isFocused ? '20px' : '10px',
    transition: 'width 0.3s ease, padding-left 0.3s ease',
  };

  const handleDrop = useCallback(
    (result: DropResult) => {
      dispatch(dndBoard(result, meetingId));
    },
    [dispatch, meetingId],
  );
  const openBoardFormDialog = useCallback(() => {
    dispatch(openDialog('boardFormDialog', { meetingId } as IBoardFormProps));
  }, [dispatch, meetingId]);

  const isMeetingMember = loggedUser && meeting && isMemberPartOfMeeting(loggedUser, meeting);

  const activeBoards = boards.filter((board) => !board.deleted);

  return (
    <div className="col-12">
      <div className="border-radius-1 lightGray-bg p-2 flex aic jcsb">
        <div className="flex aic">
          <DragDropContext onDragEnd={handleDrop}>
            {activeBoards.map(
              (board, index) =>
                board && (
                  <Droppable droppableId={board.id} key={board.id} direction="horizontal" type="column">
                    {(provided) => (
                      <div style={{ display: 'flex' }} {...provided.droppableProps} ref={provided.innerRef}>
                        <Draggable key={`draggable-row-${board.id}`} draggableId={board.id} index={index}>
                          {(provided) => (
                            <div
                              title={board.name}
                              key={board.id}
                              role="rowgroup"
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              ref={provided.innerRef}
                              onClick={() => currentBoardId(board.id)}
                            >
                              <a style={{ background: `${board.color}` }} className={'btn btn-lg f-14 px-2 bold'}>
                                {board.name}
                              </a>
                            </div>
                          )}
                        </Draggable>
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                ),
            )}
          </DragDropContext>
          {showAllBoards &&
            archivedBoards.map((board) => (
              <div style={{ display: 'flex' }} key={board.id}>
                <div title={board.name} key={board.id} onClick={() => currentBoardId(board.id)}>
                  <a style={{ background: `${board.color}`, opacity: 0.2 }} className={'btn btn-lg f-14 px-2 bold'}>
                    {board.deleted && <FaArchive className="mr-1" />}
                    {board.name}
                  </a>
                </div>
              </div>
            ))}

          {!!showCreateButton && isMeetingMember && (
            <a
              className="btn btn-lg f-14 white-bg px-2 bold popup_button"
              title="Create new board"
              onClick={openBoardFormDialog}
            >
              <i className="icon icon-plus" /> Create new board
            </a>
          )}
        </div>
        {boards.length > 0 && (
          <div className="flex aic">
            <div style={{ position: 'relative' }}>
              <input
                style={inputAnimationStyles}
                id="filter-tasks"
                name="filter-tasks"
                type="text"
                placeholder="Search Cards..."
                value={filterText}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onChange={(e) => dispatch(filterTasks(e.target.value))}
              />
              <i
                className="icon-close"
                onClick={() => dispatch(filterTasks(''))}
                style={{
                  cursor: 'pointer',
                  position: 'absolute',
                  top: '16.5px',
                  right: '10px',
                  fontSize: '10px',
                }}
              />
            </div>
            <div style={{ position: 'relative', marginLeft: '8px', zIndex: 112 }}>
              <ReactDatePicker
                autoComplete="off"
                selected={dueDateFilter}
                onChange={(date: Date) => dispatch(filterTasksByDueDate(date))}
                placeholderText={!dueDateFilter ? 'Filter by due date...' : dueDateFilter.toLocaleDateString()}
                customInput={<input style={{ height: '44px' }} />}
              />
              <i
                className="icon-close"
                onClick={() => dispatch(filterTasksByDueDate(null))}
                style={{
                  cursor: 'pointer',
                  position: 'absolute',
                  top: '16.5px',
                  right: '10px',
                  fontSize: '10px',
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
